import React from 'react';
import { MagnifyingGlassIcon, ShoppingCartIcon, CheckIcon } from '@heroicons/react/24/outline';
import {
  Field,
  Select,
  TextInput,
  EmptyState,
  Spinner,
  StatusPill,
  CARD,
  BORDER,
  SURFACE_CARD,
  TEXT_MUTED,
  TEXT_PRIMARY,
  btn,
} from '../components';
import { formatPhoneNumber, formatCurrency } from '../components/format';
import { COUNTRIES, NUMBER_TYPES, PLATFORM_DID_PRICE } from './constants';

/**
 * Search for and buy new numbers.
 *
 * The original rendered an 11-column table where most columns were carrier
 * capability flags shown as raw ✓/- glyphs in fixed boxes. Capabilities are
 * chips on the row now, and the table is a card grid that doesn't need
 * horizontal scrolling.
 *
 * The State and City filter inputs were wired to state but never appended to
 * the query — typing in them did nothing. State is now sent; City is dropped
 * because the search API doesn't accept it.
 */
const BuyNumbersTab = ({
  filters,
  setFilters,
  onSearch,
  searching,
  results,
  selection,
  onBuyOne,
  onBuySelected,
  purchasingNumber,
}) => {
  const update = (patch) => setFilters({ ...filters, ...patch });
  const isUS = filters.country === 'US' || filters.country === 'CA';

  return (
    <div className="space-y-5">
      <div className={`${CARD} p-5`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Country">
            <Select value={filters.country} onChange={(e) => update({ country: e.target.value })}>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Number type">
            <Select
              value={filters.numberType}
              onChange={(e) => update({ numberType: e.target.value })}
            >
              {NUMBER_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Area code" hint="Optional">
            <TextInput
              value={filters.areaCode}
              onChange={(e) => update({ areaCode: e.target.value })}
              placeholder="e.g. 415"
              inputMode="numeric"
              maxLength={5}
            />
          </Field>

          <Field label="Contains digits" hint="Optional">
            <TextInput
              value={filters.contains}
              onChange={(e) => update({ contains: e.target.value })}
              placeholder="e.g. 1234"
              inputMode="numeric"
            />
          </Field>

          {/* State only applies where the carrier supports it. It used to be
              rendered for every country and never sent at all. */}
          {isUS && (
            <Field label="State" hint="Two-letter code, optional">
              <TextInput
                value={filters.state}
                onChange={(e) => update({ state: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder="e.g. CA"
                maxLength={2}
              />
            </Field>
          )}

          <Field label="Results">
            <Select
              value={filters.limit}
              onChange={(e) => update({ limit: Number(e.target.value) })}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="flex items-center gap-3 mt-5">
          <button onClick={onSearch} disabled={searching} className={btn('primary')}>
            {searching ? <Spinner size="xs" inherit /> : <MagnifyingGlassIcon className="w-4 h-4" />}
            {searching ? 'Searching…' : 'Search numbers'}
          </button>
          <p className={`text-[12px] ${TEXT_MUTED}`}>
            {formatCurrency(PLATFORM_DID_PRICE)}/month per number
          </p>
        </div>
      </div>

      {searching ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : results.length === 0 ? (
        <EmptyState
          icon={MagnifyingGlassIcon}
          title="No numbers yet"
          description="Choose a country and search to see numbers available to buy."
        />
      ) : (
        <>
          <div className="flex items-center gap-3">
            <button onClick={selection.togglePage} className={btn('secondary', 'sm')}>
              {selection.pageAllSelected ? 'Clear page' : 'Select all'}
            </button>
            <p className={`text-[13px] ${TEXT_MUTED}`}>
              {results.length} available
              {selection.count > 0 ? ` · ${selection.count} selected` : ''}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {results.map((n) => {
              const checked = selection.isSelected(n);
              const busy = purchasingNumber === n.phoneNumber;
              return (
                <div
                  key={n.phoneNumber}
                  className={`${CARD} p-4 transition-colors ${
                    checked ? 'ring-1 ring-orange-500 border-orange-500' : ''
                  }`}
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => selection.toggle(n)}
                      className="mt-1 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-orange-600 focus:ring-2 focus:ring-orange-500/40 bg-white dark:bg-[#1e1e1e]"
                    />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-[15px] font-semibold tabular-nums ${TEXT_PRIMARY}`}>
                        {formatPhoneNumber(n.phoneNumber)}
                      </span>
                      <span className={`block text-[12px] mt-0.5 ${TEXT_MUTED}`}>
                        {[n.region || n.locality, n.numberType && String(n.numberType).replace('_', '-')]
                          .filter(Boolean)
                          .join(' · ') || 'Available'}
                      </span>
                    </span>
                  </label>

                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {['voice', 'sms', 'mms', 'fax'].map(
                      (cap) =>
                        n.capabilities?.[cap] && (
                          <StatusPill key={cap} label={cap.toUpperCase()} tone="neutral" size="sm" />
                        )
                    )}
                  </div>

                  <button
                    onClick={() => onBuyOne(n)}
                    disabled={busy}
                    className={`${btn('secondary', 'sm')} w-full mt-3`}
                  >
                    {busy ? <Spinner size="xs" inherit /> : null}
                    {busy ? 'Buying…' : `Buy · ${formatCurrency(PLATFORM_DID_PRICE)}/mo`}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Bulk bar. Sticky at the bottom so the count and total stay visible
          while scrolling a long result list. */}
      {selection.count > 0 && (
        <div
          className={`sticky bottom-4 ${SURFACE_CARD} border ${BORDER} rounded-xl shadow-lg p-3 flex items-center gap-3`}
        >
          <CheckIcon className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <p className={`text-[13px] min-w-0 flex-1 ${TEXT_PRIMARY}`}>
            <span className="font-semibold">{selection.count}</span> selected ·{' '}
            <span className={TEXT_MUTED}>
              {formatCurrency(selection.count * PLATFORM_DID_PRICE)}/month
            </span>
          </p>
          <button onClick={selection.clear} className={btn('ghost', 'sm')}>
            Clear
          </button>
          <button onClick={onBuySelected} className={btn('primary', 'sm')}>
            <ShoppingCartIcon className="w-4 h-4" />
            Buy {selection.count}
          </button>
        </div>
      )}
    </div>
  );
};

export default BuyNumbersTab;
